// Pure helpers for the Net Worth tab. No React and no Graph calls: Finance.js
// loads the lists with the user's token and hands the rows in.
//
// Net worth = household cash + investments + manual assets − liabilities.
//
// It is the HOUSEHOLD's number (Marc, 2026-10-01). FIG's operating account is
// pre-tax business cash, not household wealth, so it is carried beside the
// figure — "FIG operating cash", and "Incl. business" = household + FIG — and
// never inside it. Monduras stays in the household.
// Trust (client funds) is in neither number: only the masks below are ever
// summed, and a row whose Context is Trust is skipped outright.
//
// These lists are this tab's own. The Runway header keeps its own in
// accounts.js (it still counts FIG's account as money to run on).

import { INVEST_MASKS } from './accounts';

export const HOUSEHOLD_CASH_MASKS = ['2634', '2564', '9650'];
export const BUSINESS_MASK        = '1758';   // FIG operating
export const RETIREMENT_MASKS = ['8223'];
export const BROKERAGE_MASKS  = ['6181', '9454'];

export const MISMATCH_TOLERANCE = 1;   // dollars — holdings vs balance, per account
export const ACTIVITY_LIMIT     = 20;

const day   = v => (v || '').slice(0, 10);   // string-safe — no new Date() on a stored date
const cents = n => Math.round((n ?? 0) * 100);
const sum   = (rows, f) => rows.reduce((s, r) => s + f(r), 0);

// Calendar arithmetic on a bare 'YYYY-MM-DD'. Built and read in UTC on both
// sides, so the local timezone never gets a say.
export function shiftDay(yyyymmdd, delta) {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

// Finance_Holdings and Finance_InvestmentTxns name their account by lookup id,
// not by mask. Finance_Accounts carries both.
export function maskByAccountId(accounts) {
  const map = {};
  for (const a of accounts || []) if (a.id && a.Mask) map[String(a.id)] = a.Mask;
  return map;
}

// ── Manual assets ─────────────────────────────────────────────────────────────
// Which allocation bucket a manual asset belongs to. AssetType has no value for
// an investment account, so the ones Plaid can't reach (a 401k, an outside
// brokerage) are entered as "Other" and say what they are at the start of
// Notes: "Retirement. …" or "Brokerage. …". Only an Other row is read that way
// — a Metal or Vehicle stays what its type says — and the full stop is part of
// the tag, so a note that merely opens with the word doesn't qualify.
export function assetBucket(a) {
  if (a.AssetType === 'Metal')   return 'metals';
  if (a.AssetType === 'Vehicle') return 'vehicles';
  if (a.AssetType === 'Other') {
    const notes = (a.Notes || '').trimStart().toLowerCase();
    if (notes.startsWith('retirement.')) return 'retirement';
    if (notes.startsWith('brokerage.'))  return 'brokerage';
  }
  return 'other';
}

// Value is always stored positive; the page subtracts AssetType Liability.
// Value 0 (or blank) means "not yet valued": the row stays out of net worth
// and is counted, so the gap is said out loud.
// investmentTotal is the tagged retirement + brokerage accounts: money the
// headline's breakdown counts as investments rather than assets.
export function summarizeAssets(assets) {
  const out = {
    valued: [], liabilities: [], unvalued: [],
    byBucket: { retirement: 0, brokerage: 0, metals: 0, vehicles: 0, other: 0 },
    assetTotal: 0, liabilityTotal: 0,
  };
  for (const a of assets || []) {
    if (!(a.Value > 0)) { out.unvalued.push(a); continue; }
    if (a.AssetType === 'Liability') {
      out.liabilities.push(a);
      out.liabilityTotal += a.Value;
    } else {
      out.valued.push(a);
      out.assetTotal += a.Value;
      out.byBucket[assetBucket(a)] += a.Value;
    }
  }
  out.investmentTotal = out.byBucket.retirement + out.byBucket.brokerage;
  out.net = out.assetTotal - out.liabilityTotal;
  return out;
}

// ── Holdings ──────────────────────────────────────────────────────────────────
// { 'YYYY-MM-DD': { mask: total value } }
export function holdingsTotals(holdings, maskById) {
  const out = {};
  for (const h of holdings || []) {
    const d = day(h.Date), mask = maskById[String(h.AccountLookupId)];
    if (!d || !mask || !Number.isFinite(h.Value)) continue;
    if (!out[d]) out[d] = {};
    out[d][mask] = (out[d][mask] ?? 0) + h.Value;
  }
  return out;
}

// ── Daily snapshots ───────────────────────────────────────────────────────────
// One entry per Finance_Balances date. An investment account with no balance
// row that day falls back to that day's holdings total. Any of the six
// household accounts with neither is listed in `missing`, and the day is not
// `complete`: its net worth can't be stated honestly, so it isn't stated at all.
// FIG's balance rides along as `business` (null when there is no row that day)
// and has no say in `complete` — a missing FIG balance costs the business
// lines only.
export function snapshots(balances, holdingsByDay = {}) {
  const byDay = {}, updated = {};
  for (const r of balances || []) {
    const d = day(r.Date);
    if (!d || r.Context === 'Trust' || !r.Mask) continue;
    if (!byDay[d]) byDay[d] = {};
    byDay[d][r.Mask] = r;
    // The last write to any of the day's rows — the evening syncs overwrite the row in place.
    if (r.Modified && !(updated[d] >= r.Modified)) updated[d] = r.Modified;
  }

  return Object.keys(byDay).sort().map(date => {
    const rows = byDay[date], byMask = {}, missing = [], fallback = [];
    for (const mask of HOUSEHOLD_CASH_MASKS) {
      if (Number.isFinite(rows[mask]?.Current)) byMask[mask] = rows[mask].Current;
      else missing.push(mask);
    }
    for (const mask of INVEST_MASKS) {
      const held = holdingsByDay[date]?.[mask];
      if (Number.isFinite(rows[mask]?.Current)) byMask[mask] = rows[mask].Current;
      else if (Number.isFinite(held)) { byMask[mask] = held; fallback.push(mask); }
      else missing.push(mask);
    }
    const total = masks => sum(masks, m => byMask[m] ?? 0);
    return {
      date, byMask, missing, fallback,
      complete: missing.length === 0,
      cash: total(HOUSEHOLD_CASH_MASKS),
      investments: total(INVEST_MASKS),
      business: Number.isFinite(rows[BUSINESS_MASK]?.Current) ? rows[BUSINESS_MASK].Current : null,
      updated: updated[date] || null,
    };
  });
}

// Household net worth for one snapshot; null when the day can't be stated.
export const netWorthOf = (snap, assetNet) => snap && snap.complete ? snap.cash + snap.investments + assetNet : null;

// Household + FIG operating cash. null when either side is missing.
export function inclBusinessOf(snap, assetNet) {
  const household = netWorthOf(snap, assetNet);
  return household !== null && snap.business !== null ? household + snap.business : null;
}

// One point per calendar day from the first snapshot to the last. A day the
// cron missed, or a day with an account missing, is null — a gap in the line,
// never an interpolated value. Manual assets are held flat at today's value.
export function netWorthSeries(snaps, assetNet) {
  if (!snaps.length) return [];
  const byDate = Object.fromEntries(snaps.map(s => [s.date, s]));
  const out = [];
  for (let d = snaps[0].date; d <= snaps[snaps.length - 1].date; d = shiftDay(d, 1)) {
    out.push({ date: d, value: netWorthOf(byDate[d], assetNet) });
  }
  return out;
}

// The change at a point against the prior stated point (a missed day is
// skipped, never read as zero). null at the first stated point.
export function priorChange(series, i) {
  if (!series[i] || series[i].value === null) return null;
  for (let j = i - 1; j >= 0; j--) {
    if (series[j].value !== null) return { from: series[j].date, delta: series[i].value - series[j].value };
  }
  return null;
}

// ── Scorecard windows ─────────────────────────────────────────────────────────
// Day · Month · Year · Since start, each a pair of COMPLETE snapshots: the
// latest, and the one it is measured against.
//   day   — the previous snapshot (whatever its date: a missed day shows as "vs Oct 1")
//   month — the last snapshot before the 1st of the latest's month
//   year  — the last snapshot before Jan 1 of the latest's year
//   start — the first snapshot
// When history does not reach back far enough, month and year fall back to
// the first snapshot and say so (`since: true`) rather than pretend. With a
// single snapshot every window has no `from`.
export const SCORECARD_WINDOWS = [
  { key: 'day', title: 'Day' }, { key: 'month', title: 'Month' }, { key: 'year', title: 'Year' }, { key: 'start', title: 'Since start' },
];
export function scorecardWindows(snaps) {
  const stated = (snaps || []).filter(s => s.complete);
  const to = stated[stated.length - 1] || null;
  const first = stated[0] || null;
  const lastBefore = (date) => { let hit = null; for (const s of stated) if (s.date < date) hit = s; return hit; };
  return SCORECARD_WINDOWS.map(w => {
    let from = null, since = false;
    if (to && stated.length > 1) {
      if (w.key === 'day')   from = stated[stated.length - 2];
      if (w.key === 'month') from = lastBefore(`${to.date.slice(0, 7)}-01`);
      if (w.key === 'year')  from = lastBefore(`${to.date.slice(0, 4)}-01-01`);
      if (w.key === 'start') { from = first; since = true; }
      if (!from) { from = first; since = true; }
    }
    return { ...w, from, to, since };
  });
}

// Market lens, Day card. Since 10/11 the evening syncs write each day's close
// into that day's row, so the next morning's row repeats it and "vs the
// previous snapshot" reads $0. Walk back from the latest complete snapshot to
// the most recent one whose investments differ by more than MARKET_DRIFT
// (cents drift is not a move) and measure against that. `close` is the first
// row after it, the first to hold the newer value. Nothing within
// MARKET_LOOKBACK days → the plain Day window, unchanged.
export const MARKET_DRIFT    = 1;    // dollars
export const MARKET_LOOKBACK = 10;   // calendar days
export function marketDayWindow(dayWindow, snaps) {
  const to = dayWindow?.to;
  if (!to) return dayWindow;
  const stated = (snaps || []).filter(s => s.complete && s.date < to.date);
  const floor = shiftDay(to.date, -MARKET_LOOKBACK);
  let close = to.date;
  for (let i = stated.length - 1; i >= 0 && stated[i].date >= floor; i--) {
    if (Math.abs(to.investments - stated[i].investments) > MARKET_DRIFT) return { ...dayWindow, from: stated[i], since: false, close };
    close = stated[i].date;
  }
  return dayWindow;
}

// The market close a day's holdings are priced at: the latest PriceAsOf among
// that day's securities. Cash rows are left out (a sweep "price" is always 1
// and can carry a later date), and so is any date after the day itself.
// null when the day has no dated security.
export function priceDateOn(holdings, date) {
  let hit = null;
  for (const h of holdings || []) {
    if (day(h.Date) !== date || h.IsCash === true) continue;
    const p = day(h.PriceAsOf);
    if (p && p <= date && !(hit >= p)) hit = p;
  }
  return hit;
}

// Household net worth change over a window, split into what moved: cash and
// investments. Manual assets are held at one current value, so they never
// contribute (the headline formula on both ends cancels them out).
//   → { delta, pct, cash, investments } or null when either end is missing
export function netWorthChange(from, to, assetNet) {
  if (!from || !to) return null;
  const a = netWorthOf(from, assetNet), b = netWorthOf(to, assetNet);
  if (a === null || b === null) return null;
  return { delta: b - a, pct: a > 0 ? (b - a) / a : null, cash: to.cash - from.cash, investments: to.investments - from.investments };
}

// ── Market lens ───────────────────────────────────────────────────────────────
// What the investments earned, with the money Marc put in or took out set
// aside. Measured on the three Chase accounts only: they are priced daily.
// The manual accounts tagged Retirement./Brokerage. hold one current value,
// so they never move and would only dilute the % (Marc, 2026-10-16); they are
// in neither the gain nor the base. Never cash, metals, vehicles, FIG or Trust.
//
// Flows are Finance_InvestmentTxns rows of Type transfer or cash, dated after
// the window's start and up to its end, on an investment account. Amount
// keeps Plaid's sign — positive is cash OUT of the account — so net inflow is
// −Σ Amount. Buys, sells, dividends, interest and fees are performance.
//
//   gain = end value − start value − net inflow
//   pct  = Modified Dietz: gain ÷ (start value + Σ flow × share of the window
//          still ahead of it); a deposit on the last day weighs nothing.
export const FLOW_TYPES = new Set(['transfer', 'cash']);

const utc = d => { const [y, m, dd] = d.split('-').map(Number); return Date.UTC(y, m - 1, dd); };
const daysApart = (a, b) => Math.round((utc(b) - utc(a)) / 86400000);

export function marketChange(from, to, assetInfo, invTxns, maskById) {
  if (!from || !to || !from.complete || !to.complete) return null;
  const v0 = from.investments, v1 = to.investments;   // assetInfo is not read: manual accounts sit outside the Market lens
  const span = Math.max(daysApart(from.date, to.date), 1);
  let netIn = 0, weighted = 0, flows = 0;
  for (const t of invTxns || []) {
    const d = day(t.Date);
    if (!FLOW_TYPES.has(t.Type) || !INVEST_MASKS.includes(maskById[String(t.AccountLookupId)] || '')) continue;
    if (!(d > from.date && d <= to.date) || !Number.isFinite(t.Amount)) continue;
    const inflow = -t.Amount;
    netIn += inflow;
    weighted += inflow * (span - daysApart(from.date, d)) / span;
    flows++;
  }
  const gain = v1 - v0 - netIn;
  const base = v0 + weighted;
  return { gain, pct: base > 0 ? gain / base : null, netIn, flows };
}

// Holdings as a cross-check on the balance-based figure, same date only.
// `off` lists the accounts where the two disagree by more than the tolerance.
export function holdingsCheck(snap, holdingsByDay) {
  const held = snap && holdingsByDay[snap.date];
  if (!held) return null;
  const total = sum(INVEST_MASKS, m => held[m] ?? 0);
  const off = INVEST_MASKS.filter(m =>
    Number.isFinite(held[m]) && Number.isFinite(snap.byMask[m]) && Math.abs(held[m] - snap.byMask[m]) > MISMATCH_TOLERANCE);
  return { total, off };
}

// ── Allocation ────────────────────────────────────────────────────────────────
// Cash held inside an investment account stays in that account's bucket.
// Manual accounts tagged Retirement or Brokerage join the linked accounts in
// those buckets. Cash is household cash: FIG's operating account is not a segment.
// Liabilities are not a segment either; the tab prints them under the bar.
export function allocation(snap, assetInfo) {
  const of = masks => sum(masks, m => snap.byMask[m] ?? 0);
  const buckets = [
    { key: 'cash',       label: 'Cash',       value: of(HOUSEHOLD_CASH_MASKS) },
    { key: 'retirement', label: 'Retirement', value: of(RETIREMENT_MASKS) + assetInfo.byBucket.retirement },
    { key: 'brokerage',  label: 'Brokerage',  value: of(BROKERAGE_MASKS)  + assetInfo.byBucket.brokerage },
    { key: 'metals',     label: 'Metals',     value: assetInfo.byBucket.metals },
    { key: 'vehicles',   label: 'Vehicles',   value: assetInfo.byBucket.vehicles },
    { key: 'other',      label: 'Other',      value: assetInfo.byBucket.other },
  ];
  const total = sum(buckets, b => Math.max(b.value, 0));
  return { total, buckets: buckets.map(b => ({ ...b, share: total > 0 ? Math.max(b.value, 0) / total : 0 })) };
}

// ── Holdings table ────────────────────────────────────────────────────────────
// Rows for the latest holdings date, biggest first. A cash row has no cost
// basis (the column is blank, and blank is not 0), so its gain/loss is null —
// the table prints "—", never 0% or 100%.
export function holdingsTable(holdings, maskById) {
  let date = '';
  for (const h of holdings || []) if (day(h.Date) > date) date = day(h.Date);
  if (!date) return null;

  const todays = holdings.filter(h => day(h.Date) === date);
  const total = sum(todays, h => h.Value ?? 0);
  const rows = todays.map(h => {
    const isCash = h.IsCash === true;
    const costBasis = !isCash && Number.isFinite(h.CostBasis) ? h.CostBasis : null;
    const gain = costBasis !== null ? (h.Value ?? 0) - costBasis : null;
    return {
      id: h.id,
      ticker: h.Ticker || '',
      name: h.Title || '',
      mask: maskById[String(h.AccountLookupId)] || '',
      isCash,
      quantity: h.Quantity,
      price: h.Price,
      value: h.Value ?? 0,
      costBasis,
      gain,
      gainPct: gain !== null && costBasis > 0 ? gain / costBasis : null,
      weight: total > 0 ? (h.Value ?? 0) / total : 0,
      priceAsOf: day(h.PriceAsOf) && day(h.PriceAsOf) < date ? day(h.PriceAsOf) : null,
    };
  }).sort((a, b) => b.value - a.value);
  return { date, total, rows };
}

// Tickers Plaid reports as cash positions (the brokerage sweep funds).
export function sweepTickers(holdings) {
  const set = new Set();
  for (const h of holdings || []) if (h.IsCash === true && h.Ticker) set.add(h.Ticker);
  return set;
}

// ── Investment activity ───────────────────────────────────────────────────────
// Amount is signed the way Plaid signs it: positive = cash out (buy, fee),
// negative = cash in (sell, dividend, interest). Rows come back with an
// absolute amount and a direction instead.
//
// Default view, newest first, ACTIVITY_LIMIT lines:
//   • a dividend/interest and a buy of the SAME ticker, same day, same account,
//     same absolute amount collapse into one 'reinvest' line;
//   • what is left of the sweep funds' buys and sells is hidden — cash settling
//     inside the account, not a decision.
// showAll returns every raw row, untouched.
export function activity(txns, maskById, sweep, { showAll = false, limit = ACTIVITY_LIMIT } = {}) {
  const row = (t, kind = t.Type) => ({
    id: t.id,
    date: day(t.Date),
    mask: maskById[String(t.AccountLookupId)] || '',
    kind,
    ticker: t.Ticker || '',
    amount: Math.abs(t.Amount ?? 0),
    direction: kind === 'reinvest' || !t.Amount ? null : t.Amount < 0 ? 'in' : 'out',
  });
  const newestFirst = (a, b) => b.date.localeCompare(a.date) || Number(b.id) - Number(a.id);

  const all = (txns || []).filter(t => day(t.Date));
  if (showAll) return all.map(t => row(t)).sort(newestFirst);

  const pairKey = t => `${day(t.Date)}|${t.AccountLookupId}|${t.Ticker || ''}|${Math.abs(cents(t.Amount))}`;
  const buys = {};
  for (const t of all) {
    if (t.Type !== 'buy' || !t.Ticker) continue;
    const k = pairKey(t);
    if (!buys[k]) buys[k] = [];
    buys[k].push(t);
  }

  const used = new Set(), out = [];
  for (const t of all) {
    if (t.Type !== 'dividend' && t.Type !== 'interest') continue;
    const buy = t.Ticker && buys[pairKey(t)]?.find(b => !used.has(b.id));
    if (!buy) continue;
    used.add(buy.id).add(t.id);
    out.push(row(t, 'reinvest'));
  }
  for (const t of all) {
    if (used.has(t.id)) continue;
    if ((t.Type === 'buy' || t.Type === 'sell') && sweep.has(t.Ticker)) continue;
    out.push(row(t));
  }
  return out.sort(newestFirst).slice(0, limit);
}
